import * as THREE from 'three';
import type { XRTargetRaySpace } from 'three';
import type { SessionConfig } from '../setup/wizard';
import type { DominantHand } from '../config/constants';

export type ControllerPair = {
  controllerA: THREE.Object3D;
  controllerB: THREE.Object3D;
};

/**
 * userData key holding the handedness ('left' | 'right' | 'none') currently bound to a
 * controller object by Three.js. Kept in sync via {@link trackControllerHandedness}.
 */
const CONTROLLER_HANDEDNESS_KEY = 'handedness';

/**
 * Mirror Three.js's controller↔input-source binding onto the controller object's userData.
 *
 * Three.js assigns each input source to a controller *slot* (getController(0)/(1)) on the
 * `inputsourceschange` event, reusing the first vacant slot — so the slot a hand lands in is
 * NOT stable across a Meta-button suspend/resume (the sources are removed then re-added, and
 * the re-add order decides the slot). The `session.inputSources` array order is a *separate*
 * list that can disagree with this binding. Since the sword pose is driven by the controller
 * object's matrix, the only reliable way to know which physical hand a controller object tracks
 * is the binding Three.js itself uses — which it reports via these `connected`/`disconnected`
 * events. Resolve hand→controller through this, never through `session.inputSources` indexing.
 */
export function trackControllerHandedness(controller: XRTargetRaySpace): void {
  controller.addEventListener('connected', (e) => {
    controller.userData[CONTROLLER_HANDEDNESS_KEY] = e.data.handedness;
  });
  controller.addEventListener('disconnected', () => {
    delete controller.userData[CONTROLLER_HANDEDNESS_KEY];
  });
}

function controllerHandedness(controller: THREE.Object3D): string | undefined {
  return controller.userData[CONTROLLER_HANDEDNESS_KEY] as string | undefined;
}

/** Controller object Three.js currently binds to `hand`; falls back to controllerA if unknown. */
export function findControllerForHand(
  hand: string,
  controllerA: THREE.Object3D,
  controllerB: THREE.Object3D,
): THREE.Object3D {
  if (controllerHandedness(controllerA) === hand) return controllerA;
  if (controllerHandedness(controllerB) === hand) return controllerB;
  return controllerA;
}

/** Pick which controller holds the sword (dominant hand) and which is off-hand. */
export function resolveControllers(
  cfg: SessionConfig,
  controllers: ControllerPair,
): { swordController: THREE.Object3D; offHandController: THREE.Object3D } {
  const swordController = findControllerForHand(
    cfg.dominantHand,
    controllers.controllerA,
    controllers.controllerB,
  );
  const offHandController =
    swordController === controllers.controllerA ? controllers.controllerB : controllers.controllerA;
  return { swordController, offHandController };
}

export function findInputSource(session: XRSession, hand: DominantHand): XRInputSource {
  for (const src of session.inputSources) {
    if (src.handedness === hand) return src;
  }
  const first = Array.from(session.inputSources)[0];
  if (!first) throw new Error('no XR input sources available');
  return first;
}

export function findInputSourceOrNull(
  session: XRSession,
  hand: DominantHand,
): XRInputSource | null {
  for (const src of session.inputSources) {
    if (src.handedness === hand) return src;
  }
  return null;
}
